/**
 * Schémas des fichiers de réglage de web/data. Un fichier invalide bloque le démarrage :
 * pas de valeurs de repli silencieuses (implementation_ue §9).
 */
import { z } from 'zod';

const vec3 = z.tuple([z.number(), z.number(), z.number()]);
const quat = z.tuple([z.number(), z.number(), z.number(), z.number()]);
const color = vec3;
const positive = z.number().positive();
const nonNegative = z.number().min(0);
const provenance = z.unknown().optional();

export const shipIds = ['taka', 'aegis', 'hydra'] as const;
export type ShipId = (typeof shipIds)[number];

const shipProfile = z.object({
  label: z.string(),
  role: z.string(),
  health: positive,
  cruiseMaxSpeed: positive,
  boostMaxSpeed: positive,
  reverseMaxSpeed: positive,
  cruiseAcceleration: positive,
  boostAcceleration: positive,
  reverseAcceleration: positive,
  lateralThrustScale: z.number().min(0).max(1.5),
  verticalThrustScale: z.number().min(0).max(1.5),
  yawScale: z.number().min(0).max(2),
  pitchScale: z.number().min(0).max(2),
  rollScale: z.number().min(0).max(2),
  primary: z.enum(['laser', 'missile']),
  secondary: z.enum(['missile', 'swarm']),
  laser: z.object({
    damage: positive,
    range: positive,
    interval: positive,
    beamColor: color,
    beamWidth: positive,
    impact: z.enum(['standard', 'heavy']),
    /** Chaleur ajoutée par tir (1 = surchauffe). */
    heatPerShot: nonNegative,
    /** Faisceaux par tir (laser à dispersion) et leur écart angulaire. */
    pellets: z.number().int().min(1).optional(),
    spreadDeg: nonNegative.optional(),
  }).strict().nullable(),
  missiles: z.object({ initial: z.number().int().min(0), max: z.number().int().min(0) }).strict(),
  swarm: z.object({ initial: z.number().int().min(0), max: z.number().int().min(0), burst: z.number().int().min(0) }).strict(),
  hardpoints: z.object({
    guns: z.array(vec3).min(1),
    laserOrigin: vec3,
    missile: vec3,
    swarmBase: vec3,
    swarmPairStep: vec3,
    engines: z.array(vec3),
  }).strict(),
  collisionRadius: positive,
}).strict();

export const shipsSchema = z.object({
  _provenance: provenance,
  flight: z.object({
    releaseDeceleration: nonNegative,
    brakeDeceleration: nonNegative,
    brakeThrustFactor: z.number().min(0).max(1),
    forwardOvershootDeceleration: positive,
    reverseOvershootDeceleration: positive,
    reverseSpeedThreshold: nonNegative,
    boostInputThreshold: z.number().min(0).max(1),
    reverseInputThreshold: z.number().min(0).max(1),
    turningBoost: nonNegative,
    gamepadLookRateDeg: positive,
    rollRateDeg: positive,
    arenaLimit: positive,
    /** Accélération angulaire (°/s²) : inertie de rotation du joueur. */
    angularAccelerationDeg: positive,
    /** Manche virtuel à la souris : déplacement (en degrés souris) pour une déviation complète. */
    mouseJoystick: z.object({ rangeDeg: positive, deadZone: z.number().min(0).max(0.5), exponent: positive, maxRateScale: z.number().min(0.1).max(1), recenterRate: nonNegative, recenterDelay: nonNegative }).strict(),
    /** Accélérateur persistant : vitesse de réglage au clavier (par s), cran de molette, limite arrière. */
    throttle: z.object({ rate: positive, wheelStep: positive, reverseLimit: z.number().min(-1).max(0) }).strict(),
    /** Jauge de boost en secondes de boost (phase A). */
    boostEnergy: z.object({ capacity: positive, drain: positive, regen: positive, regenDelay: nonNegative, minToStart: nonNegative }).strict(),
  }).strict(),
  ships: z.object({ taka: shipProfile, aegis: shipProfile, hydra: shipProfile }).strict(),
}).strict();

export const weaponsSchema = z.object({
  _provenance: provenance,
  lock: z.object({
    range: positive,
    minDot: z.number().min(-1).max(1),
    detectionDuration: nonNegative,
    acquisitionDuration: positive,
    activeBonus: nonNegative,
    previousBonus: nonNegative,
    originForward: nonNegative,
  }).strict(),
  missile: z.object({
    damage: positive,
    radius: positive,
    interval: positive,
    initialSpeed: positive,
    maxSpeed: positive,
    homingAcceleration: nonNegative,
    life: positive,
    collisionRadius: positive,
  }).strict(),
  swarm: z.object({
    damage: positive,
    radius: positive,
    interval: positive,
    maxActive: z.number().int().positive(),
    initialSpeed: positive,
    maxSpeed: positive,
    homingAcceleration: nonNegative,
    life: positive,
    collisionRadius: positive,
    proximityFuse: nonNegative,
    homingDelay: nonNegative,
    homingStagger: nonNegative,
    cruiseTurnRateDeg: positive,
    terminalTurnRateDeg: positive,
    terminalDistance: positive,
    leadScale: z.number().min(0).max(1.5),
    maxLeadTime: nonNegative,
    lossGrace: nonNegative,
    lossDot: z.number().min(-1).max(1),
    lossDuration: positive,
    lostLife: positive,
    radialComponent: nonNegative,
    emission: z.enum(['crown', 'sphere']),
    sphereNoHomingTime: nonNegative,
    replacementRange: positive,
    replacementMinDot: z.number().min(-1).max(1),
  }).strict(),
  laserHeat: z.object({ coolRate: positive, coolDelay: nonNegative, overheatLockout: nonNegative, resumeAt: z.number().min(0).max(1) }).strict(),
  pickup: z.object({
    amount: z.number().int().positive(),
    combatLife: positive,
    arenaLife: positive,
    attractRadius: positive,
    attractSpeed: positive,
    collectRadius: positive,
  }).strict(),
}).strict();

const enemyBase = {
  label: z.string(),
  health: positive,
  maxSpeed: positive,
  acceleration: positive,
  turnRateDeg: positive,
  preferredDistance: positive,
  retreatDistance: nonNegative,
  fireRange: positive,
  projectileSpeed: positive,
  fireInterval: positive,
  projectileDamage: positive,
  projectileCount: z.number().int().positive(),
  spreadDeg: nonNegative,
  chargeDuration: nonNegative,
  scale: positive,
  collisionRadius: positive,
  weaponForward: nonNegative,
  requiredFacingDot: z.number().min(-1).max(1),
  boltColor: color,
  /** Modèle 3D utilisé (un rôle peut réutiliser le modèle d'un autre) et échelle appliquée. */
  model: z.enum(['interceptor', 'marksman', 'heavy', 'drone', 'turret']),
  modelScale: positive,
};

export const enemyRoles = ['interceptor', 'marksman', 'heavy', 'drone', 'turret'] as const;
export type EnemyRole = (typeof enemyRoles)[number];

export const enemiesSchema = z.object({
  _provenance: provenance,
  common: z.object({
    leadFactor: nonNegative,
    maxLeadTime: nonNegative,
    blockedRetryDelay: positive,
    avoidanceMinDistance: positive,
    avoidanceRadiusFactor: positive,
    avoidanceNormalWeight: nonNegative,
    navigationLimit: positive,
    hitFlashDuration: positive,
    releaseDeceleration: nonNegative,
    brakeDeceleration: nonNegative,
    turningBoost: nonNegative,
    carrierKeepOutMargin: nonNegative,
  }).strict(),
  roles: z.object({
    interceptor: z.object({
      ...enemyBase,
      leadTime: nonNegative,
      passLateral: nonNegative,
      passVertical: nonNegative,
      passTimeFactor: positive,
      passTimeMin: positive,
      passTimeMax: positive,
      breakawayDuration: positive,
      blockedBreakawayDuration: positive,
    }).strict(),
    marksman: z.object({ ...enemyBase, approachMargin: nonNegative, chargeExtraCooldown: nonNegative }).strict(),
    heavy: z.object({
      ...enemyBase,
      engagementWindow: positive,
      blindSpotLateral: nonNegative,
      blindSpotVertical: nonNegative,
    }).strict(),
    drone: z.object({
      ...enemyBase,
      detonationRadius: positive,
      blastRadius: positive,
      detonationDamage: positive,
      leadTime: nonNegative,
      armTime: nonNegative,
      /** Nuée : orbite autour du joueur, tirs légers, piqués annoncés et limités en nombre. */
      orbitRadius: positive,
      orbitSpeed: positive,
      orbitHeight: nonNegative,
      firstDiveDelay: nonNegative,
      diveCooldownMin: positive,
      diveCooldownMax: positive,
      diveTelegraph: nonNegative,
      diveMaxTime: positive,
      maxConcurrentDives: z.number().int().min(0),
      /** Piqué et tirs seulement quand le drone est devant le joueur (produit scalaire avec son axe). */
      diveFrontDot: z.number().min(-1).max(1),
      fireFrontDot: z.number().min(-1).max(1),
    }).strict(),
    /** Tourelle de forteresse (phase D) : fixe, pivote vers le joueur, tirs en rafale ; objectif du siège. */
    turret: z.object({
      ...enemyBase,
      /** Tirs par rafale et intervalle entre deux tirs d'une rafale (s). */
      burst: z.number().int().positive(),
      burstInterval: positive,
    }).strict(),
  }).strict(),
}).strict();

const bossPhase = z.object({
  maxSpeed: positive,
  fireInterval: positive,
  damage: positive,
  preferredDistance: positive,
  retreatDistance: nonNegative,
}).strict();

export const bossPhases = ['opening', 'assault', 'overdrive'] as const;
export type BossPhase = (typeof bossPhases)[number];

export const bossSchema = z.object({
  _provenance: provenance,
  health: positive,
  assaultThreshold: z.number().min(0.1).max(0.9),
  overdriveThreshold: z.number().min(0.05).max(0.85),
  turnRateDeg: positive,
  acceleration: positive,
  projectileSpeed: positive,
  fireRange: positive,
  requiredFacingDot: z.number().min(-1).max(1),
  collisionRadius: positive,
  weaponForward: nonNegative,
  phaseEntryRecovery: nonNegative,
  phases: z.object({ opening: bossPhase, assault: bossPhase, overdrive: bossPhase }).strict(),
  openingPass: z.object({ lateral: nonNegative, vertical: nonNegative, timeFactor: positive, timeMin: positive, timeMax: positive }).strict(),
  volley: z.object({
    interval: positive,
    firstDelay: nonNegative,
    chargeDuration: positive,
    recoveryDuration: nonNegative,
    damage: positive,
    overdriveDamage: positive,
    projectileSpeed: positive,
    range: positive,
    sideOffset: nonNegative,
  }).strict(),
  special: z.object({
    interval: positive,
    firstDelay: nonNegative,
    chargeDuration: positive,
    recoveryDuration: nonNegative,
    damage: positive,
    projectileSpeed: positive,
    range: positive,
    laneSpacing: positive,
    laneCount: z.number().int().positive(),
    corridorMode: z.enum(['hold', 'attached']),
  }).strict(),
  /** Drones appelés par le boss en Assaut et Surcharge. */
  summon: z.object({ interval: positive, firstDelay: nonNegative, count: z.number().int().min(0), radius: positive }).strict(),
}).strict().refine((b) => b.overdriveThreshold < b.assaultThreshold, 'Le seuil de Surcharge doit être inférieur à celui d’Assaut.');

export const sectorIds = ['openField', 'rockRidge', 'transitGate', 'amberWell', 'monolith'] as const;
export type SectorId = (typeof sectorIds)[number];

export const wavesSchema = z.object({
  _provenance: provenance,
  firstWaveDelay: nonNegative,
  intermission: nonNegative,
  repairPerWave: nonNegative,
  spawnClearance: positive,
  bossSpawnClearance: positive,
  formationLateral: nonNegative,
  formationVertical: nonNegative,
  alternativeOffset: positive,
  alternativeStep: nonNegative,
  bossArrival: z.enum(sectorIds),
  bossSpawnDistance: positive,
  bossSpawnLift: z.number(),
  droneJitter: nonNegative,
  scoring: z.object({
    points: z.object({ drone: nonNegative, interceptor: nonNegative, marksman: nonNegative, heavy: nonNegative, turret: nonNegative, boss: nonNegative }).strict(),
    multiplierStep: nonNegative,
    multiplierMax: z.number().min(1),
    streakWindow: positive,
  }).strict(),
  waves: z.array(z.object({
    id: z.string(),
    formationRadius: positive,
    arrival: z.enum(sectorIds),
    composition: z.array(z.enum(enemyRoles)).min(1),
  }).strict()).min(1),
}).strict();

export const arenaSchema = z.object({
  _provenance: provenance,
  halfExtent: positive,
  playerLimit: positive,
  enemyLimit: positive,
  spawnLimit: positive,
  boundaryWarning: positive,
  beaconDistance: positive,
  sectors: z.array(z.object({
    id: z.enum(sectorIds),
    label: z.string(),
    center: vec3,
    color,
    visitRadius: positive,
    role: z.string(),
  }).strict()).length(sectorIds.length),
  obstacles: z.array(z.object({
    id: z.string(),
    sector: z.enum(sectorIds),
    kind: z.enum(['cover', 'resource', 'landmark']),
    position: vec3,
    rotation: quat,
    span: positive,
    semiAxes: vec3,
    variant: z.number().int().min(0),
  }).strict()),
  amberResource: z.object({ lift: z.number(), offsetStep: z.number(), lateralRatio: z.number() }).strict(),
  /**
   * Station Vigie dans l'arène (modèle vigie, collision data/stationHull.json, bras vers la baie) ; absente des
   * zones sans station alliée. `span` : taille du repli procédural sans modèle.
   */
  station: z.object({ position: vec3, rotation: quat, span: positive }).strict().optional(),
}).strict();

export const cameraSchema = z.object({
  _provenance: provenance,
  baseFov: z.number().min(40).max(140),
  maxFov: z.number().min(40).max(150),
  baseDistance: positive,
  maxDistance: positive,
  height: z.number(),
  positionLagSpeed: positive,
  positionLagMax: nonNegative,
  rotationLagSpeed: positive,
  fovInterpSpeed: positive,
  distanceInterpSpeed: positive,
  reverseFovBonus: z.number(),
  reverseDistanceBonus: z.number(),
  brakeFovDelta: z.number(),
  brakeDistanceDelta: z.number(),
  boostPulseDuration: nonNegative,
  boostPulseFov: z.number(),
  boostPulseDistance: z.number(),
  reducedMotionScale: z.number().min(0).max(1),
  shake: z.object({
    damageAmplitude: nonNegative,
    damageDuration: positive,
    boostAmplitude: nonNegative,
    boostDuration: positive,
    explosionAmplitude: nonNegative,
    explosionRange: positive,
    frequency: positive,
  }).strict(),
  speedStreaks: z.object({ startSpeed: nonNegative, lengthFactor: nonNegative, maxLength: positive }).strict(),
  /** Décalage de caméra opposé à la rotation (m par rad/s) : le vaisseau « vire » dans le cadre. */
  turnOffset: nonNegative,
  turnOffsetMax: nonNegative,
  /** Inclinaison visuelle des coques en virage et en glissement (rendu seulement). */
  bank: z.object({ maxDeg: nonNegative, fromYaw: nonNegative, fromLateral: nonNegative, pitchMaxDeg: nonNegative, fromPitch: nonNegative, response: positive }).strict(),
  feedback: z.object({
    cruiseWeight: nonNegative,
    cruiseEaseExponent: positive,
    boostWeight: nonNegative,
    lateralWeight: nonNegative,
  }).strict(),
}).strict();

export const hudSchema = z.object({
  _provenance: provenance,
  markerRange: positive,
  onScreenBudget: z.number().int().positive(),
  offScreenBudget: z.number().int().positive(),
  radarRange: positive,
  pickupMarkerRange: positive,
  arenaPickupMarkerRange: positive,
  hitConfirmDuration: positive,
  killConfirmDuration: positive,
  damageFeedbackDuration: positive,
  repairFeedbackDuration: positive,
  lineWidth: positive,
  lineWidthEnhanced: positive,
  threatLineWidth: positive,
  threatLineWidthEnhanced: positive,
  /** Teinte émissive des coques ennemies par rôle (lisibilité sur le fond spatial). */
  roleTint: z.object({ interceptor: z.number().min(0).max(1), marksman: z.number().min(0).max(1), heavy: z.number().min(0).max(1), drone: z.number().min(0).max(1), boss: z.number().min(0).max(1) }).strict(),
  colors: z.object({
    player: color,
    acquisition: color,
    heavy: color,
    boss: color,
    hostile: color,
    resource: color,
  }).strict(),
}).strict();

/** Enveloppes convexes des rochers (cube unité), une par modèle rock_N : collision = géométrie visible. */
export const rocksSchema = z.object({
  _provenance: provenance,
  variants: z.array(z.array(vec3).min(4)),
}).strict();

const box = z.object({ id: z.string(), center: vec3, half: vec3 }).strict();

export const dockIds = ['station', 'carrier'] as const;
export type DockId = (typeof dockIds)[number];

/**
 * Point d'amarrage (porteur, baie de la station) : coque en boîtes de collision, tunnel traversant
 * (entrée en poupe, sortie en proue), zone d'approche, séquences d'appontage et de catapulte, service.
 */
const dockShape = {
  _provenance: provenance,
  label: z.string(),
  position: vec3,
  rotation: quat,
  parts: z.array(box).min(1),
  tunnel: z.object({ halfWidth: positive, halfHeight: positive, halfLength: positive }).strict(),
  pad: vec3,
  approach: z.object({ center: vec3, half: vec3 }).strict(),
  dockMaxSpeed: positive,
  dockMinAlignment: z.number().min(-1).max(1),
  dockDuration: positive,
  launchDuration: positive,
  launchSpeed: positive,
  launchMinDocked: nonNegative,
  keepOutRadius: positive,
  service: z.object({ repairRate: nonNegative, missileInterval: positive, swarmRate: nonNegative }).strict(),
  launchInvulnerability: nonNegative,
  /** Point d'attache du bras vers la station (repère du dock) ; défaut : haut des boîtes au centre. */
  armMount: vec3.optional(),
};

export const dockSchema = z.object(dockShape).strict();

/** Porteur allié (phase B) ; porte aussi les règles de réapparition (réserves par mission). */
export const carrierSchema = z.object({
  ...dockShape,
  respawn: z.object({ reserves: z.number().int().min(0), delay: nonNegative, autoLaunchDelay: nonNegative }).strict(),
}).strict();

/** Baie d'amarrage de la station : armurerie complète, hangar, missions. */
export const stationSchema = dockSchema;

/** Scénarios de mission (choisis à quai, lancés au décollage). */
export const missionsSchema = z.object({
  _provenance: provenance,
  /** Ordre d'affichage dans les menus de dock. */
  order: z.array(z.string()).min(1),
  scenarios: z.record(z.string(), z.object({
    label: z.string(),
    description: z.string(),
    /** Identifiants de vagues de waves.json, dans l'ordre. */
    waves: z.array(z.string()),
    /** Le croiseur ennemi conclut la mission. */
    boss: z.boolean(),
    victoryBonus: z.number().int().min(0),
    /** Docks où la mission est proposée. */
    docks: z.array(z.enum(dockIds)).min(1),
    /** Zone où elle se déroule (zones.json) : choisie depuis une autre zone, elle commence par un saut. */
    zone: z.string(),
    /**
     * clear : vagues (et boss) ; turrets : détruire les tourelles de la forteresse de la zone ; escort : amener le
     * convoi de la zone au bout de sa route.
     */
    objective: z.enum(['clear', 'turrets', 'escort']),
  }).strict()),
}).strict().superRefine((m, ctx) => {
  for (const id of m.order) if (!m.scenarios[id]) ctx.addIssue({ code: 'custom', path: ['order'], message: `scénario inconnu : ${id}` });
  for (const [id, s] of Object.entries(m.scenarios)) {
    if (s.waves.length === 0 && !s.boss) ctx.addIssue({ code: 'custom', path: ['scenarios', id], message: 'ni vague ni boss' });
  }
});

/**
 * Forteresse ennemie (phase D) : boîtes de collision dans son repère, cœur lumineux (décor) et emplacements de
 * tourelles (repère local, `normal` = face d'appui, vers l'extérieur).
 */
const fortressSchema = z.object({
  label: z.string(),
  position: vec3,
  rotation: quat,
  parts: z.array(box).min(1),
  core: z.object({ center: vec3, radius: positive }).strict(),
  turrets: z.array(z.object({ id: z.string(), position: vec3, normal: vec3 }).strict()).min(1),
}).strict();

/**
 * Zones du monde (phase D) : une arène par zone, docks présents, ambiance. On passe d'une zone à l'autre par
 * un saut du porteur, depuis son menu de missions.
 */
export const zonesSchema = z.object({
  _provenance: provenance,
  /** Zone de départ. */
  home: z.string(),
  order: z.array(z.string()).min(1),
  zones: z.record(z.string(), z.object({
    label: z.string(),
    description: z.string(),
    /** Le porteur est toujours présent : c'est lui qui saute d'une zone à l'autre. */
    docks: z.array(z.enum(dockIds)).min(1).refine((d) => d.includes('carrier'), 'le porteur doit être présent'),
    /** « default » : arena.json ; sinon arène propre à la zone. */
    arena: z.union([z.literal('default'), arenaSchema]),
    /** Ciel affiché ; `level` multiplie art.skyLevel. */
    sky: z.object({ texture: z.string(), level: z.number().min(0).max(2) }).strict(),
    /** Teinte du soleil du décor et de l'ambiance. */
    keyColor: color,
    ambientSky: color,
    /** Étalonnage propre à la zone (art.render) : exposition multipliée, saturation ajoutée, teinte globale (0..360, densité 0..100). */
    grade: z.object({ exposure: positive.optional(), saturation: z.number().min(-100).max(100).optional(), hue: z.number().min(0).max(360).optional(), density: z.number().min(0).max(100).optional() }).strict().optional(),
    /** Sortie des chasseurs qui sautent dans la zone (portail juste derrière), en vol, cap `rotation`. */
    arrival: z.object({ position: vec3, rotation: quat }).strict(),
    fortress: fortressSchema.optional(),
    /**
     * Phase E : `surface` = zone au sol d'une planète, rejointe seulement depuis son orbite (`orbitOf`) par le
     * porteur (descente atmosphérique) ; les modules de saut des chasseurs ne vont que d'espace à espace.
     */
    kind: z.enum(['space', 'surface']),
    orbitOf: z.string().optional(),
    /** Brouillard (nébuleuse, atmosphère) : couleur et densité exponentielle (1/m). */
    fog: z.object({ color, density: positive }).strict().optional(),
    /** Portée du radar et des marqueurs multipliée (brouillage) ; 1 par défaut. */
    radarScale: z.number().min(0.1).max(1).optional(),
    /** Étoiles fixes affichées (faux sous une atmosphère). */
    stars: z.boolean().optional(),
    /** Orages ioniques : éclairs aléatoires dans le brouillard. */
    storms: z.boolean().optional(),
    /** Planète en fond (orbite) : sphère texturée, couche de nuages, halo d'atmosphère. */
    planet: z.object({ position: vec3, radius: positive, texture: z.string(), clouds: z.string(), atmosphere: color, spinDeg: z.number() }).strict().optional(),
    /**
     * Sol (surface) : relief procédural déterministe (graine), partagé par la collision (champ de hauteurs) et le
     * rendu. `flats` : plateaux arasés (bases, zones d'arrivée).
     */
    ground: z.object({
      size: positive,
      resolution: z.number().int().min(8).max(256),
      baseY: z.number(),
      amplitude: nonNegative,
      seed: z.number().int(),
      flats: z.array(z.object({ center: z.tuple([z.number(), z.number()]), radius: positive, height: z.number() }).strict()),
      low: color,
      high: color,
    }).strict().optional(),
    /** Escorte : convoi allié qui suit `path` (points monde) à `speed` m/s ; coque et rayon d'impact. */
    convoy: z.object({ label: z.string(), path: z.array(vec3).min(2), speed: positive, health: positive, radius: positive }).strict().optional(),
  }).strict()),
}).strict().superRefine((z_, ctx) => {
  if (!z_.zones[z_.home]) ctx.addIssue({ code: 'custom', path: ['home'], message: `zone inconnue : ${z_.home}` });
  for (const id of z_.order) if (!z_.zones[id]) ctx.addIssue({ code: 'custom', path: ['order'], message: `zone inconnue : ${id}` });
  for (const [id, zone] of Object.entries(z_.zones)) {
    if (zone.kind === 'surface') {
      const orbit = zone.orbitOf ? z_.zones[zone.orbitOf] : undefined;
      if (!orbit || orbit.kind !== 'space') ctx.addIssue({ code: 'custom', path: ['zones', id, 'orbitOf'], message: 'une zone de surface a besoin de sa zone d\'orbite (espace)' });
      if (!zone.ground) ctx.addIssue({ code: 'custom', path: ['zones', id, 'ground'], message: 'une zone de surface a besoin d\'un sol' });
    }
  }
});

/** Coque de collision de la Station Vigie : boîtes (centre, demi-tailles) dans le repère du modèle. */
export const stationHullSchema = z.object({
  _provenance: provenance,
  cell: positive,
  bounds: z.tuple([vec3, vec3]),
  /** Collier du moyeu où s'attache le bras de la baie (repère du modèle). */
  armAnchor: vec3,
  boxes: z.array(z.array(z.number()).length(6)).min(1),
}).strict();

const livery = z.object({
  base: color,
  accent: color,
  detail: z.number().min(0).max(1),
  rim: nonNegative,
  stripe: nonNegative,
  stripeFrequency: nonNegative,
}).strict();

/** Direction artistique : livrées de faction stylisées et réglages visuels. */
export const artSchema = z.object({
  _provenance: provenance,
  liveries: z.object({ player: livery, interceptor: livery, marksman: livery, heavy: livery, drone: livery, boss: livery, carrier: livery, station: livery }).strict(),
  /** bands : bandes émissives ; twoTone : dessus / ventre + liseré ; tinted : textures d'origine teintées ; original : textures d'origine. */
  liveryStyle: z.enum(['bands', 'twoTone', 'tinted', 'original']),
  /** Part des reflets du ciel sur les coques (le ciel n'est pas préfiltré : 1 = reflets quasi miroirs). */
  shipEnvironmentIntensity: z.number().min(0).max(2),
  skyLevel: z.number().min(0).max(2),
  /** Soleil du décor et rebond (intensités Babylon). */
  keyLight: z.number().min(0).max(20),
  bounceLight: z.number().min(0).max(20),
  /**
   * Lumière d'appoint qui suit la caméra (lumière de personnage) : les vaisseaux restent lisibles même à
   * contre-jour du soleil du décor. `pitchDeg` : inclinaison vers le bas par rapport à l'axe de visée.
   */
  cameraLight: z.object({ intensity: z.number().min(0).max(10), color, pitchDeg: z.number().min(0).max(80) }).strict(),
  /**
   * Détail de surface des docks (textures répétables triplanaires) : taille d'un motif de coque et de hangar (m),
   * part de la couleur de texture, force du relief, marge du volume « hangar » autour du tunnel (m).
   */
  dockDetail: z.object({ hullTile: positive, hangarTile: positive, albedo: z.number().min(0).max(1), normal: z.number().min(0).max(3), wallMargin: nonNegative }).strict(),
  /** Ambiance hémisphérique (ciel / sol). */
  ambient: z.object({ intensity: z.number().min(0).max(5), sky: color, ground: color }).strict(),
  rockAlbedo: color,
  hitStop: nonNegative,
  explosionScale: positive,
  /**
   * Rendu (palier A, src/render/postfx.ts) : exposition et contraste après tone mapping ACES, saturation (-100..100),
   * bloom des valeurs HDR au-dessus du seuil, vignette, part du ciel préfiltré dans l'éclairage des PBR, ombres en
   * cascade (portée en m, obscurité, répartition des cascades), occlusion à l'écran (rayon et portée en m),
   * disque solaire (taille à 18 km, intensité HDR, force du reflet d'objectif).
   */
  render: z.object({
    exposure: positive,
    contrast: positive,
    saturation: z.number().min(-100).max(100),
    bloom: z.object({ threshold: nonNegative, weight: nonNegative, kernel: positive, scale: z.number().min(0.1).max(1) }).strict(),
    vignette: z.number().min(0).max(10),
    environmentIntensity: z.number().min(0).max(5),
    shadows: z.object({ maxZ: positive, darkness: z.number().min(0).max(1), lambda: z.number().min(0).max(1) }).strict(),
    ssao: z.object({ radius: positive, strength: positive, maxZ: positive }).strict(),
    sun: z.object({ size: positive, intensity: nonNegative, flare: z.number().min(0).max(2) }).strict(),
  }).strict(),
}).strict();

/** `jump` : module de saut entre zones, propre aux chasseurs lourds (objets restreints par châssis). */
export const slotIds = ['primary', 'secondary', 'shield', 'engine', 'hull', 'utility', 'jump'] as const;
export type SlotId = (typeof slotIds)[number];
export const itemFamilies = ['laser', 'missile', 'swarm', 'shield', 'engine', 'hull', 'utility', 'jump'] as const;
export type ItemFamily = (typeof itemFamilies)[number];

const laserSpec = shipProfile.shape.laser.unwrap();

/** Effets d'un objet d'équipement ; tout effet absent laisse la valeur du châssis. */
const itemEffects = z.object({
  /** Remplace le laser du châssis. */
  laser: laserSpec.optional(),
  /** Stock de missiles : multiplicateur puis ajout (arrondi, au moins `minMissiles`). */
  missileCapacityScale: positive.optional(),
  missileCapacityAdd: z.number().int().optional(),
  missileDamageScale: positive.optional(),
  missileRadiusScale: positive.optional(),
  swarmBurst: z.number().int().positive().optional(),
  swarmDamageScale: positive.optional(),
  swarmEmission: z.enum(['crown', 'sphere']).optional(),
  shield: z.object({
    capacity: positive,
    /** Délai sans dégât avant la régénération, puis débit (points/s). */
    regenDelay: nonNegative,
    regenRate: positive,
    /** Délai de régénération après un bouclier rompu (à zéro). */
    breakDelay: nonNegative,
  }).strict().optional(),
  speedScale: positive.optional(),
  accelerationScale: positive.optional(),
  boostCapacityScale: positive.optional(),
  boostRegenScale: positive.optional(),
  healthAdd: z.number().optional(),
  agilityScale: positive.optional(),
  /** Réparation d'urgence : sous `threshold` de coque, rend `amount` ; `charges` par sortie (rechargées au porteur). */
  emergencyRepair: z.object({ threshold: z.number().min(0.05).max(0.9), amount: positive, charges: z.number().int().positive() }).strict().optional(),
  radarRangeScale: positive.optional(),
  laserCoolScale: positive.optional(),
  /** Module de saut : durée de charge (s) avant le saut vers une autre zone de l'espace, hors mission. */
  jumpDrive: z.object({ charge: positive }).strict().optional(),
}).strict();

const item = z.object({
  family: z.enum(itemFamilies),
  label: z.string(),
  description: z.string(),
  price: z.number().int().min(0),
  /** Châssis autorisés (tous si absent). */
  chassis: z.array(z.enum(shipIds)).optional(),
  effects: itemEffects,
}).strict();

/** Phase C : objets d'équipement, chargement par défaut des châssis, économie des crédits. */
export const equipmentSchema = z.object({
  _provenance: provenance,
  economy: z.object({
    /** Crédits par point de score. */
    scoreRate: nonNegative,
    /** Part des crédits de score conservée en défaite et en abandon. */
    defeatFactor: z.number().min(0).max(1),
    abandonFactor: z.number().min(0).max(1),
    startingCredits: z.number().int().min(0),
  }).strict(),
  minMissiles: z.number().int().min(0),
  items: z.record(z.string(), item),
  /** Objets payants vendus et montables au porteur (la station a tout ; les objets gratuits sont partout). */
  carrierStock: z.array(z.string()),
  defaults: z.object({
    taka: z.record(z.enum(slotIds), z.string()),
    aegis: z.record(z.enum(slotIds), z.string()),
    hydra: z.record(z.enum(slotIds), z.string()),
  }).strict(),
}).strict().superRefine((e, ctx) => {
  for (const ship of shipIds) {
    for (const slot of slotIds) {
      const id = e.defaults[ship][slot];
      if (!id) ctx.addIssue({ code: 'custom', path: ['defaults', ship, slot], message: 'emplacement sans objet par défaut' });
      else if (!e.items[id]) ctx.addIssue({ code: 'custom', path: ['defaults', ship, slot], message: `objet inconnu : ${id}` });
      else if (e.items[id]!.price !== 0) ctx.addIssue({ code: 'custom', path: ['defaults', ship, slot], message: `l'objet par défaut doit être gratuit : ${id}` });
    }
  }
  for (const id of e.carrierStock) if (!e.items[id]) ctx.addIssue({ code: 'custom', path: ['carrierStock'], message: `objet inconnu : ${id}` });
});

export type ShipsData = z.infer<typeof shipsSchema>;
export type ShipProfile = ShipsData['ships']['taka'];
export type FlightData = ShipsData['flight'];
export type WeaponsData = z.infer<typeof weaponsSchema>;
export type EnemiesData = z.infer<typeof enemiesSchema>;
export type EnemyProfile = EnemiesData['roles'][EnemyRole];
export type BossData = z.infer<typeof bossSchema>;
export type WavesData = z.infer<typeof wavesSchema>;
export type ArenaData = z.infer<typeof arenaSchema>;
export type CameraData = z.infer<typeof cameraSchema>;
export type HudData = z.infer<typeof hudSchema>;
export type RocksData = z.infer<typeof rocksSchema>;
export type CarrierData = z.infer<typeof carrierSchema>;
export type DockData = z.infer<typeof dockSchema>;
export type MissionsData = z.infer<typeof missionsSchema>;
export type ScenarioData = MissionsData['scenarios'][string];
export type ArtData = z.infer<typeof artSchema>;
export type ZonesData = z.infer<typeof zonesSchema>;
export type StationHullData = z.infer<typeof stationHullSchema>;
export type ZoneData = ZonesData['zones'][string];
export type FortressData = z.infer<typeof fortressSchema>;
export type Livery = z.infer<typeof livery>;
export type EquipmentData = z.infer<typeof equipmentSchema>;
export type EquipmentItem = z.infer<typeof item>;
export type ItemEffects = z.infer<typeof itemEffects>;
export type LaserSpec = z.infer<typeof laserSpec>;

export interface GameData {
  ships: ShipsData;
  weapons: WeaponsData;
  enemies: EnemiesData;
  boss: BossData;
  waves: WavesData;
  arena: ArenaData;
  camera: CameraData;
  hud: HudData;
  rocks: RocksData;
  carrier: CarrierData;
  station: DockData;
  missions: MissionsData;
  art: ArtData;
  equipment: EquipmentData;
  zones: ZonesData;
  stationHull: StationHullData;
}

export const schemas = {
  ships: shipsSchema,
  weapons: weaponsSchema,
  enemies: enemiesSchema,
  boss: bossSchema,
  waves: wavesSchema,
  arena: arenaSchema,
  camera: cameraSchema,
  hud: hudSchema,
  rocks: rocksSchema,
  carrier: carrierSchema,
  station: stationSchema,
  missions: missionsSchema,
  art: artSchema,
  equipment: equipmentSchema,
  zones: zonesSchema,
  stationHull: stationHullSchema,
} as const;

export type DataFile = keyof typeof schemas;
